import { Component } from '@angular/core';
import { SiteDashboardService } from '../../site-dashboard.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { Subscription } from 'rxjs';
import { DeviceFactory } from '../../device/device-factory/device-factory';
import * as moment from 'moment';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { TranslateService } from '@ngx-translate/core';
import { SitesService } from 'src/app/sites/sites.service';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-meter-energy-report',
	templateUrl: './meter-energy-report.component.html',
	styleUrls: ['./meter-energy-report.component.css']
})
export class MeterEnergyReportComponent {

	currentSite: any = {};
	siteSubscription: Subscription = new Subscription();
	data: any;

	readonly allYearsId = 'All-Years';
	readonly allQuartersId = 'All Quarters';
	readonly allMonthsId = 'All-Months';
	readonly allDevicesId = 'All-Devices';

	readonly periods = { MONTHLY: 'monthly', QUARTERLY: 'quarterly' };
	readonly periodTypes = [
		{ label: this.translate.instant('g.quarterly'), value: this.periods.QUARTERLY },
		{ label: this.translate.instant('g.monthly'), value: this.periods.MONTHLY },
	];

	iotEmeterTypes: any[] = [
		DeviceFactory.types.ioTEmeterEdge,
		DeviceFactory.types.ioTEmeter,
		DeviceFactory.types.ioTEmeterSp3,
		DeviceFactory.types.ioTEmeterSp1
	];

	meterEnergyReportTableConfig = {
		column: [] as ColumnsConfig[],
		data: [],
		config: {
			hasExport: true,
			hasPagination: true,
			draggable: true,
			pageSize: 100,
			fileName: this.translate.instant('site.meter_energy_report'),
			csvFileHeaderInfo: [],
		} as TableConfig
	};

	siteInstallationDate = null;
	yearList: any[] = [];
	quarterList = [];
	monthList = [];
	readonly devicesList = [
		{ label: this.translate.instant('iotemeter_site_energy_report.all_meters'), value: this.allDevicesId },
		{ label: this.translate.instant('iotemeter_site_energy_report.edge'), value: DeviceFactory.types.ioTEmeterEdge },
		{ label: this.translate.instant('iotemeter_site_energy_report.pro'), value: DeviceFactory.types.ioTEmeter },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp3'), value: DeviceFactory.types.ioTEmeterSp3 },
		{ label: this.translate.instant('iotemeter_site_energy_report.sp1'), value: DeviceFactory.types.ioTEmeterSp1 },
	];

	selectedYear: string | number;
	selectedPeriodType: string = this.periods.MONTHLY;
	selectedQuarter: string = '';
	selectedMonth: string | number = '';
	selectedDeviceType: string | number = this.allDevicesId;

	noData = false;

	constructor(
		private sitesService: SitesService,
		public siteDashboardService: SiteDashboardService,
		private translate: TranslateService,
		private sideMenuService: SideMenuService,
		private commonService: CommonService,
	) { }

	ngOnInit() {
		this.siteSubscription = this.sideMenuService.currentSite.subscribe((siteInfo: any) => {
			if (siteInfo && siteInfo.installation_date) {
				this.currentSite = siteInfo;
				this.siteInstallationDate = moment.utc(siteInfo.installation_date).startOf('day').toDate();
				const { yearList, currentYear } = this.siteDashboardService.prepareYearsList(this.yearList, this.siteInstallationDate);
				this.yearList = [{ label: this.translate.instant('site_performance.truck_year_all'), id: this.allYearsId }, ...yearList];
				this.selectedYear = currentYear;

				this.fillMeterEnergyReportReportCols();
				this.setListQuarters();
				this.setListMonths();
				this.loadAllData(); 
			}
		});
	}

	selectYear() {
		this.setListQuarters();
		this.setListMonths();
		this.loadAllData();
	}

	selectPeriodType() {
		this.setListQuarters();
		this.setListMonths();
		this.loadAllData();
	}

	setListQuarters() {
		const minDate = this.siteInstallationDate;

		let quarters: any[] = [this.allQuartersId];

		if (+this.selectedYear === moment().year() || +this.selectedYear === moment(minDate).year()) {
			for (let quarter = 1; quarter <= 4; quarter++) {
				if ((+this.selectedYear === moment().year() && quarter > moment().quarter()) || (quarter < moment(minDate).quarter() && +this.selectedYear === moment(minDate).year()))
					continue;

				quarters.push(`Q${quarter}`);
			}

			this.selectedQuarter = +this.selectedYear === moment().year() ? `Q${moment().format('Q')}` : quarters[1];
			return this.quarterList = quarters;
		}

		quarters = [this.allQuartersId, 'Q1', 'Q2', 'Q3', 'Q4']

		this.selectedQuarter = quarters[1];
		this.quarterList = quarters;
	}

	setListMonths() {
		const months: any[] = [{ value: this.allMonthsId, label: this.translate.instant('g.all_months') }];

		if (+this.selectedYear === moment().year()) {
			for (let month = 0; month <= moment().month(); month++) {
				months.push({ value: month + 1, label: moment().month(month).format('MMMM') });
			}

			const currentMonthInList = (months.filter(month => month.label == moment().format('MMMM')))[0].value;

			this.selectedMonth = structuredClone(currentMonthInList);
			this.monthList = months;
			return months;
		}

		const allMonths = moment.months();
		let monthCounter = 1;

		for (const month of allMonths) {
			months.push({ value: monthCounter++, label: month })
		}

		this.monthList = months;
		this.selectedMonth = months[1].value;

		return months;
	}

	isAllYearsSelected() {
		return this.selectedYear === this.allYearsId;
	}

	fillMeterEnergyReportReportCols() {
		let meterEnergyReportCol: ColumnsConfig[] = [
			{ key: "serial_number", name: this.translate.instant("iotemeter.devices.iotemeter_sn"), type: 'link' },
			// IoTEmeter ID (site-defined ID #)
			{ key: 'iotemeter_id', name: this.translate.instant("iotemeter.devices.iotemeter_id"), type: 'string' },
			{ key: 'iotemeter_model', name: this.translate.instant("iotemeter.devices.iotemeter_model"), type: 'string' },
			{ key: 'panel_id', name: this.translate.instant('iotemeter.devices.panel_equipment'), type: 'string'},
			{ key: 'load_type', name: this.translate.instant("iotemeter.devices.load_type_equipment"), type: 'string' },
			{ key: 'total_kWhrs', name: this.translate.instant("utilization.total_kWhrs"), type: 'number' },
			{ key: 'average_daily_kWhrs', name: this.translate.instant("utilization.average_Daily_kWhrs"), type: 'number' },
			{ key: 'max_demand_kw', name: this.translate.instant("iotemeter.devices.max_demand_kW"), type: 'number' },
		];

		this.meterEnergyReportTableConfig.column = meterEnergyReportCol;

		this.meterEnergyReportTableConfig.config.csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			includeSpacing: false
		});
	}


	loadAllData() {
		const criteria = {
			siteId: this.currentSite.id,
			year: this.selectedYear,
			periodType: this.selectedPeriodType,
			quarter: this.selectedQuarter,
			month: this.selectedMonth,
			deviceType: this.selectedDeviceType,
		};

		this.sitesService.getMeterEnergyReport(criteria).subscribe((response: any) => {
			this.mergeAndFillTable(response);
		});
	}

	mergeAndFillTable(data: any[]) {
		const tableData: TableData[] = data.map((device: any) => {
			return {
				serial_number: {
					value: device.serial_number,
					link: ['/', this.currentSite.customer_id, this.currentSite.id, device.mac_address, "performance"]
				},
				iotemeter_id: { value: device.iotemeter_id ?? "test" },
				iotemeter_model: { value: this.getModelName(device.sct_device_type) },
				panel_id: { value: device.panel_id || '-' },
				load_type: { value: device.load_type || '-' },
				total_kWhrs: { value: device.total_kwh || 0 },
				average_daily_kWhrs: { value: device.avg_daily_kwh || 0 },
				max_demand_kw: { value: device.max_demand_kw || 0 }
			};
		});
		this.meterEnergyReportTableConfig.data = tableData;
	}

	getModelName(type: number): string {
		switch (type) {
			case DeviceFactory.types.ioTEmeterEdge: return 'Edge';
			case DeviceFactory.types.ioTEmeter: return 'Pro';
			case DeviceFactory.types.ioTEmeterSp1: return 'SP1';
			case DeviceFactory.types.ioTEmeterSp3: return 'SP3';
			default: return '-';
		}
	}

	ngOnDestroy() {
		this.siteSubscription.unsubscribe();
	}
}
